import React, { useState, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Translate, ArrowRight, ArrowsLeftRight, ClipboardText, CircleNotch, Sparkle, Broom, Check, TextAa, UploadSimple, FileText, X, Warning, Eye } from "@phosphor-icons/react";

const MODES = [
    { key: "snippet", label: "Snippet", maxChars: 4000, hint: "Ideal for single questions, options, short explanations." },
    { key: "long",    label: "Long-form", maxChars: 50000, hint: "For editorials, study notes, syllabus paragraphs, uploaded documents." },
];

const SOURCES = [
    { key: "text",   label: "Paste text",  icon: TextAa },
    { key: "upload", label: "Upload file", icon: UploadSimple },
];

const DIRECTIONS = {
    kn_to_en: {
        key: "kn_to_en",
        shortLabel: "Kannada → English",
        headerTitle: "Kannada → English Translation",
        inputHeader: "Kannada · ಕನ್ನಡ",
        outputHeader: "English translation",
        placeholder: "ಇಲ್ಲಿ ಕನ್ನಡ ಪಠ್ಯವನ್ನು ಅಂಟಿಸಿ… (Paste Kannada text here)",
        sample: "ಭಾರತದ ಸಂವಿಧಾನದ 356ನೇ ವಿಧಿಯನ್ನು ಸಂಬಂಧಿಸಿದಂತೆ ರಾಷ್ಟ್ರಪತಿ ಆಳ್ವಿಕೆಯ ಪ್ರಸ್ತಾವಿತ ಸನ್ನಿವೇಶಗಳನ್ನು ವಿವರಿಸಿ.",
        emptyToastMsg: "Enter some Kannada text first",
        subtitle:
            "Kannada → English tuned for KPSC/UPSC content. Proper nouns stay English, Karnataka-specific terms carry the original Kannada in brackets, tone stays exam-formal.",
        inputFont: "'Noto Sans Kannada', 'Tiro Kannada', system-ui, sans-serif",
        outputFont: "inherit",
    },
    en_to_kn: {
        key: "en_to_kn",
        shortLabel: "English → Kannada",
        headerTitle: "English → Kannada Translation",
        inputHeader: "English",
        outputHeader: "Kannada · ಕನ್ನಡ",
        placeholder: "Paste English text here…",
        sample:
            "Explain the constitutional provisions and circumstances under which President's Rule can be imposed in a State under Article 356 of the Constitution of India.",
        emptyToastMsg: "Enter some English text first",
        subtitle:
            "English → Kannada tuned for KPSC/UPSC content. Proper nouns and acronyms remain in Latin script (Aadhaar, GST, ISRO…) while civic terms use accepted Kannada equivalents.",
        inputFont: "inherit",
        outputFont: "'Noto Sans Kannada', 'Tiro Kannada', system-ui, sans-serif",
    },
};

const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

export default function AdminTranslate() {
    const [direction, setDirection] = useState("kn_to_en");
    const [source, setSource] = useState("text");
    const [mode, setMode] = useState("snippet");
    const [input, setInput] = useState("");
    const [file, setFile] = useState(null);
    const [output, setOutput] = useState("");
    const [busy, setBusy] = useState(false);
    const [meta, setMeta] = useState(null);
    const [copied, setCopied] = useState(false);
    const [extracted, setExtracted] = useState(null);
    const [usedOcr, setUsedOcr] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const activeMode = MODES.find((m) => m.key === mode);
    const dir = DIRECTIONS[direction];

    const resetOutputs = () => {
        setOutput(""); setMeta(null); setExtracted(null); setCopied(false); setUsedOcr(false); setError(null);
    };

    const flipDirection = () => {
        // Swap input/output so users can iterate quickly.
        const newDir = direction === "kn_to_en" ? "en_to_kn" : "kn_to_en";
        setDirection(newDir);
        if (output) {
            setInput(output);
        }
        resetOutputs();
    };

    const changeDirection = (key) => {
        if (key === direction) return;
        setDirection(key);
        resetOutputs();
    };

    const doTranslateText = async () => {
        const text = input.trim();
        if (!text) { toast.error(dir.emptyToastMsg); return; }
        setBusy(true); resetOutputs();
        try {
            const { data } = await api.post("/admin/translate", { text, mode, direction }, { timeout: 90_000 });
            setOutput(data.translation);
            setMeta({ chars_in: data.chars_in, words_in: data.words_in, duration_ms: data.duration_ms });
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Translation failed";
            setError(msg); toast.error(msg);
        } finally { setBusy(false); }
    };

    const doTranslateUpload = async () => {
        if (!file) { toast.error("Select a file first"); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", mode);
        fd.append("direction", direction);
        setBusy(true); resetOutputs();
        try {
            const start = await api.post("/admin/translate/upload", fd, { timeout: 60_000 });
            const jobId = start.data.job_id;
            if (!jobId) throw new Error("No job_id returned");

            // Poll every 2s up to 4 minutes
            const deadline = Date.now() + 4 * 60_000;
            while (Date.now() < deadline) {
                await new Promise((r) => setTimeout(r, 2000));
                const { data: job } = await api.get(`/admin/translate/jobs/${jobId}`);
                if (job.status === "done") {
                    const r = job.result;
                    setOutput(r.translation);
                    setMeta({ chars_in: r.chars_in, words_in: r.words_in, duration_ms: r.duration_ms });
                    setUsedOcr(!!r.used_ocr);
                    setExtracted({ ...r.source, preview: r.extracted_text });
                    setBusy(false);
                    return;
                }
                if (job.status === "error") {
                    const msg = job.error || "Translation failed";
                    setError(msg); toast.error(msg); setBusy(false); return;
                }
            }
            setError("Translation timed out after 4 minutes. Try a smaller file or use paste-text mode.");
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Upload/translation failed";
            setError(msg); toast.error(msg);
        } finally { setBusy(false); }
    };

    const doTranslate = () => (source === "text" ? doTranslateText() : doTranslateUpload());

    const copyOut = async () => {
        try {
            await navigator.clipboard.writeText(output);
            setCopied(true); toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1800);
        } catch { toast.error("Could not copy"); }
    };

    const clearAll = () => {
        setInput(""); setOutput(""); setMeta(null); setExtracted(null); setFile(null); setUsedOcr(false); setError(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    const loadSample = () => { setSource("text"); setInput(dir.sample); resetOutputs(); };

    const inputChars = input.length;
    const inputWords = input.trim() ? input.trim().split(/\s+/).length : 0;
    const overLimit = source === "text" && inputChars > activeMode.maxChars;
    const canTranslate = source === "text" ? (!!input.trim() && !overLimit) : !!file;

    const busyLabel = useMemo(() => {
        if (source === "upload") return "Extracting text & translating…";
        return direction === "en_to_kn" ? "Translating with Gemini 3.1 Pro…" : "Translating with Gemini 3.1 Pro…";
    }, [source, direction]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3 border-2 border-dashed border-[#BFD3FF] bg-blue-50/40 rounded-2xl p-5">
                <Translate size={28} weight="duotone" className="text-[#1E3A8A] shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h2 data-testid="tr-header-title" className="font-heading font-bold text-lg">{dir.headerTitle}</h2>
                    <p className="text-sm text-slate-600 mt-1">
                        Powered by Google Gemini 3.1 Pro — {dir.subtitle}
                    </p>
                </div>
            </div>

            {/* Persistent error banner (survives after toast fades) */}
            {error && (
                <div data-testid="tr-error" className="flex items-start gap-3 border-2 border-red-300 bg-red-50 rounded-2xl p-4">
                    <Warning size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-heading font-bold text-sm text-red-900">Could not complete translation</div>
                        <p className="text-xs text-red-800 mt-1">{error}</p>
                        <p className="text-[11px] text-red-700 mt-2 italic">
                            If this is a scanned or image-based PDF, the tool now automatically uses Gemini Vision OCR to read text from page images (first 3 pages). The job runs asynchronously — feel free to try again if it timed out.
                        </p>
                    </div>
                    <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800 p-1"><X size={16} weight="bold" /></button>
                </div>
            )}

            {/* Direction toggle */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">Direction</span>
                <div className="inline-flex rounded-lg border-2 border-[#BFD3FF] overflow-hidden">
                    {Object.values(DIRECTIONS).map((d) => (
                        <button
                            key={d.key}
                            data-testid={`tr-direction-${d.key}`}
                            onClick={() => changeDirection(d.key)}
                            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                                direction === d.key ? "bg-[#1E3A8A] text-white" : "bg-white text-slate-700 hover:bg-blue-50"
                            }`}
                        >
                            {d.shortLabel}
                        </button>
                    ))}
                </div>
                <button
                    data-testid="tr-direction-flip"
                    onClick={flipDirection}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-[#BFD3FF] bg-white text-xs font-semibold text-slate-700 hover:border-[#1E3A8A] hover:text-[#1E3A8A]"
                    title="Swap direction and move output → input"
                >
                    <ArrowsLeftRight size={13} weight="bold" /> Swap
                </button>
            </div>

            {/* Source selector */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">Source</span>
                {SOURCES.map((s) => (
                    <button
                        key={s.key}
                        data-testid={`tr-source-${s.key}`}
                        onClick={() => setSource(s.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors ${
                            source === s.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                        }`}
                    >
                        <s.icon size={13} weight="bold" /> {s.label}
                    </button>
                ))}
            </div>

            {/* Mode selector */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">Mode</span>
                {MODES.map((m) => (
                    <button
                        key={m.key}
                        data-testid={`tr-mode-${m.key}`}
                        onClick={() => setMode(m.key)}
                        className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors ${
                            mode === m.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                        }`}
                    >
                        {m.label}
                        <span className={`ml-2 text-[10px] font-normal ${mode === m.key ? "opacity-80" : "text-slate-500"}`}>
                            ≤ {(m.maxChars / 1000).toFixed(0)}k chars
                        </span>
                    </button>
                ))}
                <span className="text-[11px] text-slate-500 ml-2 italic">{activeMode.hint}</span>
                {source === "text" && (
                    <button data-testid="tr-sample" onClick={loadSample} className="ml-auto text-xs text-[#1E3A8A] hover:underline">
                        Try a sample
                    </button>
                )}
            </div>

            {/* Two-column editor */}
            <div className="grid md:grid-cols-2 gap-4">
                {/* LEFT — input (text or upload) */}
                <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden flex flex-col">
                    <div className="px-4 py-2.5 border-b-2 border-[#BFD3FF] flex items-center justify-between">
                        <div data-testid="tr-input-header" className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            {source === "text" ? dir.inputHeader : "Upload · PDF / DOCX / TXT"}
                        </div>
                        {source === "text" && (
                            <div className={`text-[10px] font-semibold tabular-nums ${overLimit ? "text-red-600" : "text-slate-500"}`}>
                                {inputChars}/{activeMode.maxChars} chars · {inputWords} words
                            </div>
                        )}
                    </div>

                    {source === "text" ? (
                        <textarea
                            data-testid="tr-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={dir.placeholder}
                            className="flex-1 p-4 text-sm resize-none min-h-[320px] focus:outline-none"
                            style={{ fontFamily: dir.inputFont }}
                        />
                    ) : (
                        <div className="flex-1 min-h-[320px] p-6 flex flex-col gap-4">
                            {!file ? (
                                <label className="flex-1 border-2 border-dashed border-[#BFD3FF] rounded-2xl grid place-items-center cursor-pointer hover:border-[#1E3A8A] hover:bg-blue-50/40 transition-colors">
                                    <div className="text-center px-6">
                                        <UploadSimple size={36} weight="duotone" className="text-[#1E3A8A] mx-auto mb-2" />
                                        <div className="font-heading font-bold text-sm">Drop a file or click to browse</div>
                                        <div className="text-[11px] text-slate-500 mt-1">PDF · DOCX · TXT · Max 15 MB</div>
                                        <div className="text-[10px] text-slate-400 mt-2">Scanned image PDFs are auto-processed with Vision OCR (first 3 pages).</div>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        data-testid="tr-file-input"
                                        type="file"
                                        accept=".pdf,.docx,.txt"
                                        className="hidden"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    />
                                </label>
                            ) : (
                                <div className="border-2 border-[#BFD3FF] rounded-xl p-4 flex items-start gap-3">
                                    <FileText size={30} weight="duotone" className="text-[#1E3A8A] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm truncate">{file.name}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{fmtBytes(file.size)}</div>
                                        {extracted && (
                                            <div className="mt-3 text-[11px] text-slate-600">
                                                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                                                    Extracted preview · {extracted.extracted_chars} chars
                                                </div>
                                                <div
                                                    className="max-h-40 overflow-auto text-xs bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap"
                                                    style={{ fontFamily: dir.inputFont }}
                                                >{extracted.preview}{extracted.extracted_chars > 2000 && "…"}</div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        data-testid="tr-file-clear"
                                        onClick={() => { setFile(null); setExtracted(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                                        className="text-slate-400 hover:text-red-600 p-1"
                                        title="Remove file"
                                    >
                                        <X size={16} weight="bold" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* RIGHT — output */}
                <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden flex flex-col">
                    <div className="px-4 py-2.5 border-b-2 border-[#BFD3FF] flex items-center justify-between">
                        <div data-testid="tr-output-header" className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{dir.outputHeader}</div>
                        <button
                            data-testid="tr-copy"
                            onClick={copyOut}
                            disabled={!output}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 hover:text-[#1E3A8A] disabled:opacity-30"
                        >
                            {copied ? <><Check size={11} weight="bold" /> Copied</> : <><ClipboardText size={11} weight="bold" /> Copy</>}
                        </button>
                    </div>
                    <div className="flex-1 min-h-[320px] p-4 text-sm text-slate-800 whitespace-pre-wrap relative" style={{ fontFamily: dir.outputFont }}>
                        {busy ? (
                            <div className="absolute inset-0 grid place-items-center gap-2 text-slate-500 text-xs">
                                <CircleNotch size={22} className="animate-spin text-[#1E3A8A]" />
                                <div>{busyLabel}</div>
                            </div>
                        ) : output ? (
                            <div data-testid="tr-output">{output}</div>
                        ) : (
                            <p className="text-slate-400 italic" style={{ fontFamily: "inherit" }}>Translation will appear here.</p>
                        )}
                    </div>
                    {meta && !busy && (
                        <div className="px-4 py-2 border-t-2 border-[#BFD3FF] text-[10px] text-slate-500 flex items-center gap-3">
                            <Sparkle size={11} weight="fill" className="text-gold" />
                            {meta.words_in > 0 && <>{meta.words_in} words · {meta.chars_in} chars · </>}
                            {(meta.duration_ms / 1000).toFixed(1)}s
                            {usedOcr && (
                                <span data-testid="tr-used-ocr" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 font-semibold text-[10px] uppercase tracking-wider">
                                    <Eye size={10} weight="fill" /> Vision OCR fallback
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
                <button data-testid="tr-clear" onClick={clearAll} className="btn-ghost !py-2 !px-4">
                    <Broom size={16} weight="bold" /> Clear
                </button>
                <button
                    data-testid="tr-translate"
                    onClick={doTranslate}
                    disabled={busy || !canTranslate}
                    className="ml-auto btn-primary !py-2 !px-6 disabled:opacity-50"
                >
                    {busy ? <><CircleNotch size={16} className="animate-spin" /> Translating…</> : <><ArrowRight size={16} weight="bold" /> Translate</>}
                </button>
            </div>
        </div>
    );
}
